import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closePaymentModal,
  openPaymentMethod,
} from "../../../features/slices/ModalSlice";
import newChatIcon from "../../../assets/images/newChatIcon.png";
import PaymentMethodSlider from "./PaymentMethodSlider";
import {
  initiateDepositIntent,
  reset,
} from "../../../features/slices/PaymentSlice";
import { useMediaQuery } from "react-responsive";
import {
  showChatBoxInDesktop,
  updateSelectedItem,
} from "../../../features/slices/CommonSlice";
import ErrorCard from "../../common/ErrorCard";

const toNumber = (value) => +String(value ?? "").replace(/,/g, "") || 0;

const formatInr = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// bonus the selected offer adds to this deposit: a percentage (capped at
// max_bonus_amount) or a flat amount, and nothing below the offer's min_deposit
const getOfferBonus = (offer, amount) => {
  if (!offer || !amount) return 0;
  if (toNumber(offer.min_deposit) > amount) return 0;
  const percentage = Number(offer.percentage) || 0;
  if (percentage > 0) {
    const bonus = (amount * percentage) / 100;
    const cap = Number(offer.max_bonus_amount) || 0;
    return cap > 0 ? Math.min(bonus, cap) : bonus;
  }
  return Number(offer.amount) || 0;
};

export default function DepositContent() {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const dispatch = useDispatch();
  const amountOptions = [
    {
      amount: "500",
      id: "amount1",
    },
    {
      amount: "1,000",
      id: "amount2",
    },
    {
      amount: "5,000",
      id: "amount3",
    },
    {
      amount: "10,000",
      id: "amount4",
    },
    {
      amount: "50,000",
      id: "amount5",
    },
  ];
  const modal = useSelector((state) => state.modal);
  const payment = useSelector((state) => state.payment);
  const [selectedAmount, setSelectedAmount] = useState("amount1");
  const [enteredAmount, setAmount] = useState("");
  const [openTermsId, setOpenTermsId] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [nextStep, getNextStep] = useState("");
  const [paymentId, setPaymentId] = useState(null);
  // const [offerCode, setOfferCode] = useState(null);
  const [redBorder, setRedBorder] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({});
  const [gatewayMessage, setGatewayMessage] = useState("");

  // only manual (UPI / IMPS) methods have a deposit screen; gateways are not wired yet
  const usableMethods = payment.paymentMethods.filter(
    (item) => item.status && item.isManualPayment
  );
  const amountValue = toNumber(enteredAmount);
  const minAmount = toNumber(selectedPaymentMethod.minAmonut);
  const maxAmount = toNumber(selectedPaymentMethod.maxAmonut);
  const selectedBonusOffer = payment.bonusOffers.find(
    (offer) => offer.id === selectedOffer
  );
  const offerMinDeposit = toNumber(selectedBonusOffer?.min_deposit);
  const amountError = !enteredAmount
    ? ""
    : amountValue < minAmount
    ? `Amount can't be less than ${selectedPaymentMethod.minAmonut}`
    : maxAmount && amountValue > maxAmount
    ? `Amount can't be greater than ${selectedPaymentMethod.maxAmonut}`
    : selectedBonusOffer && amountValue < offerMinDeposit
    ? `This offer needs a deposit of at least ${offerMinDeposit.toLocaleString(
        "en-IN"
      )} INR`
    : "";
  const bonusAmount = amountError
    ? 0
    : getOfferBonus(selectedBonusOffer, amountValue);
  const canProceed =
    Boolean(selectedPaymentMethod.id) &&
    Boolean(enteredAmount) &&
    !amountError;

  const selectMethod = (payment_id) => {
    const selectedMethod = payment.paymentMethods.find(
      (item) => item.id === payment_id
    );
    if (!selectedMethod) return;

    setPaymentId(payment_id);
    setGatewayMessage("");
    getNextStep(
      selectedMethod.isManualPayment ? selectedMethod.stepKey : ""
    );
    setSelectedPaymentMethod(selectedMethod);
  };

  const addCommaInAmmount = (amount) => {
    return (
      parseInt(amount.toString().replace(/[^\d]+/gi, "")) || ""
    ).toLocaleString("en-US");
  };
  const handleAmountSelect = (amount) => {
    const addedAMount = amountValue + toNumber(amount);
    if (maxAmount && addedAMount > maxAmount) return;
    let finalAmount = addCommaInAmmount(addedAMount);
    if (finalAmount.length === 7) {
      finalAmount = finalAmount.slice(0, 1) + "," + finalAmount.slice(1);
    }
    setAmount(finalAmount);
  };
  const handleSelectedOffer = (offerId) => {
    setSelectedOffer((prev) => (prev === offerId ? null : offerId));
  };
  const goToNextStep = () => {
    if (!selectedPaymentMethod.id) return;

    if (!canProceed) {
      setRedBorder(true);
      if (isMobile) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      return;
    }
    setRedBorder(false);
    setGatewayMessage("");
    dispatch(
      reset({
        amount: enteredAmount,
        amountId: selectedAmount,
      })
    );

    // AUTO / gateway methods are not wired yet — do not call createDepositRequest.
    if (!selectedPaymentMethod.isManualPayment) {
      setGatewayMessage("This gateway is not implemented");
      return;
    }

    // Deposit modal only has screens for UPI and IMPS.
    const manualStep =
      nextStep === "UPI" || nextStep === "IMPS" ? nextStep : "UPI";

    const selectedBonus = selectedBonusOffer;
    dispatch(
      openPaymentMethod({
        nextStep: manualStep,
        amount: enteredAmount,
        paymentMethodId: paymentId,
        bonusTemplateId: selectedBonus?.templateId || "",
        // offerCode: null,
      })
    );
  };

  useEffect(() => {
    if (payment.selectedAmount?.amount) {
      setAmount(payment.selectedAmount.amount);
      setSelectedAmount(payment.selectedAmount.amountId);
    }
  }, [payment.selectedAmount]);

  const setEnteredAmount = (event) => {
    let amount = addCommaInAmmount(event.target.value);
    if (amount.length === 7) {
      amount = amount.slice(0, 1) + "," + amount.slice(1);
    }
    setAmount(amount);
    setRedBorder(false);
  };

  useEffect(() => {
    if (!modal.depositData?.paymentMethodId) return;

    const selectedMethod = payment.paymentMethods.find(
      (item) => item.id === modal.depositData.paymentMethodId
    );
    if (!selectedMethod) return;

    setPaymentId(selectedMethod.id);
    getNextStep(
      selectedMethod.isManualPayment ? selectedMethod.stepKey : ""
    );
    setSelectedPaymentMethod(selectedMethod);
  }, [modal.depositData, payment.paymentMethods]);

  // coming back from the UPI / IMPS step keeps the offer that was picked
  useEffect(() => {
    const templateId = modal.depositData?.bonusTemplateId;
    if (!templateId) return;
    const offer = payment.bonusOffers.find(
      (item) => item.templateId === templateId
    );
    if (offer) setSelectedOffer(offer.id);
  }, [modal.depositData, payment.bonusOffers]);

  // record the deposit intent as soon as the screen opens with a method
  // selected (amount falls back to the method's minimum until the player types
  // one), and again (debounced) whenever the amount or method changes
  useEffect(() => {
    if (!paymentId || !selectedPaymentMethod.id) return;
    const amount = amountValue || minAmount;
    if (!amount) return;

    const timer = setTimeout(() => {
      dispatch(initiateDepositIntent({ amount, paymentMethodId: paymentId }));
    }, 600);
    return () => clearTimeout(timer);
  }, [amountValue, minAmount, paymentId, selectedPaymentMethod.id, dispatch]);

  const showChatBox = () => {
    if (isMobile) {
      dispatch(updateSelectedItem("chat-bot"));
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else dispatch(showChatBoxInDesktop());
    dispatch(closePaymentModal());
  };

  useEffect(() => {
    const activePaymentMethods = payment.paymentMethods.filter(
      (item) => item.status && item.isManualPayment
    );
    if (!activePaymentMethods.length) return;

    const firstMethod = activePaymentMethods[0];
    setSelectedPaymentMethod(firstMethod);
    setPaymentId(firstMethod.id);
    getNextStep(firstMethod.isManualPayment ? firstMethod.stepKey : "");
  }, [payment.paymentMethods]);

  return (
    <div className="row">
      <div className="col-md-6 col-lg-6 col-xl-6 col-12 deposit-col1 pt-4 pb-4">
        <div className="payment-options px-4">
          <PaymentMethodSlider
            methods={usableMethods}
            selectedId={paymentId}
            onSelect={selectMethod}
          />
        </div>
        <div className="amount-input mt-3 px-4">
          <label className="text--normal" style={{ fontWeight: "200" }}>
            Amount <span aria-hidden="true" className="required-asterisk">*</span>
          </label>
          <div className="d-flex">
            <input
              type="text"
              placeholder="Enter Amount"
              pattern="^[\d,]+$"
              className={redBorder ? "red-border" : ""}
              value={enteredAmount}
              onChange={(e) => {
                setEnteredAmount(e);
              }}
            />
            <span style={{ float: "right" }}>INR</span>
          </div>

          {selectedPaymentMethod.id ? (
            <p className="low-opacity text--small mb-0 mt-1">
              {selectedPaymentMethod.minAmonut +
                "-" +
                selectedPaymentMethod.maxAmonut}
            </p>
          ) : null}
          {payment.paymentMethods.length && !usableMethods.length ? (
            <p className="text--small mb-0 mt-2" style={{ color: "#F5CA3D" }}>
              <i className="bi bi-info-circle"></i> No deposit method is
              available right now. Please contact support.
            </p>
          ) : null}
        </div>
        <div className="amount-options mt-3 px-4">
          <div className="d-flex">
            {amountOptions.map((item, index) => (
              <button
                key={index}
                className="text-center amount"
                id={item.id}
                onClick={() => handleAmountSelect(item.amount)}
                disabled={
                  Boolean(maxAmount) &&
                  amountValue + toNumber(item.amount) > maxAmount
                }
              >
                +{item.amount}
              </button>
            ))}
          </div>
        </div>

        {!!payment.paymentMethods.length && selectedPaymentMethod.id && (
          <div className="px-4">
            <p
              className="low-opacity text--small mb-0 mt-1"
              style={{ color: "#E42D2D" }}
            >
              {amountError}
            </p>
          </div>
        )}
        {!!payment.bonusOffers.length && (
          <div className="available-offers-container mt-3">
            <label className="text--small">Available Offers</label>
            <div className="available-offers">
              {payment.bonusOffers.map((offer) => (
                <div
                  key={offer.id}
                  className={`offer-card mt-2 mb-2 ${
                    selectedOffer === offer.id ? "active-offer" : ""
                  }`}
                  onClick={() => handleSelectedOffer(offer.id)}
                >
                  <p className=" text--medium mb-0">
                    {offer.displayName}
                  </p>
                  <p className="mb-0 text--normal offer-detail">
                    {offer.displayDetail}
                  </p>
                  {offer.description &&
                  offer.description !== offer.displayDetail ? (
                    <p className="mb-0 text--small offer-detail">
                      {offer.description}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="offer-terms-toggle"
                    aria-expanded={openTermsId === offer.id}
                    onClick={(event) => {
                      // reading the terms must not select the offer
                      event.stopPropagation();
                      setOpenTermsId((prev) =>
                        prev === offer.id ? null : offer.id
                      );
                    }}
                  >
                    Terms & Conditions
                    <i
                      className={`bi ${
                        openTermsId === offer.id
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      }`}
                    />
                  </button>
                  {openTermsId === offer.id ? (
                    <ul
                      className="offer-terms"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {toNumber(offer.min_deposit) > 0 ? (
                        <li>
                          Minimum deposit{" "}
                          {toNumber(offer.min_deposit).toLocaleString("en-IN")}{" "}
                          INR
                        </li>
                      ) : null}
                      {Number(offer.wagering_multiplier) > 0 ? (
                        <li>
                          Bonus can be withdrawn after{" "}
                          {offer.wagering_multiplier}X wagering
                        </li>
                      ) : null}
                      {Number(offer.expiry_days) > 0 ? (
                        <li>Expires {offer.expiry_days} days after credit</li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="col-md-6 col-lg-6 col-xl-6 col-12 deposit-col2">
        <div
          className="mt-3 px-3"
          style={{ borderBottom: "1px solid #707070", paddingBottom: "7px" }}
        >
          <p
            className="text--normal payment-detail"
            style={{ color: "#F5CA3D" }}
          >
            Payment Details
          </p>
          <div>
            <span className="text--normal mb-0">Amount</span>
            <span
              className="text--normal phone-amount-font"
              style={{ float: "right" }}
            >
              {formatInr(amountError ? 0 : amountValue)} INR
            </span>
          </div>
          <div className="mt-1">
            <span className="text--normal mb-0">Bonus Offer</span>
            <span
              className="text--normal phone-amount-font"
              style={{ float: "right" }}
            >
              {formatInr(bonusAmount)} INR
            </span>
          </div>
        </div>
        <div className="px-3 mt-1">
          <span className="text--normal mb-0">Total Amount</span>
          <span
            className="text--normal phone-amount-font"
            style={{ float: "right" }}
          >
            {formatInr((amountError ? 0 : amountValue) + bonusAmount)} INR
          </span>
        </div>
        {Number(selectedBonusOffer?.wagering_multiplier) > 0 ? (
          <div className="mt-3 px-3 mobile-spacing">
            <p
              className="mb-0"
              style={{ fontWeight: "200", fontSize: "12px", opacity: "0.75" }}
            >
              Note:
            </p>
            <p
              className="text--normal"
              style={{ color: "#F5CA3D", fontWeight: "300", opacity: "0.75" }}
            >
              Bonus amount can be withdrawn only after{" "}
              {selectedBonusOffer.wagering_multiplier}X wagering
            </p>
          </div>
        ) : null}
        <div className="action-btns deposit-action-btns px-3">
          {gatewayMessage ? <ErrorCard error={gatewayMessage} /> : null}
          {/* <p className="text-center text--medium statement">
            Problem in making payment please contact customer support
          </p> */}
          <div className="d-flex justify-content-between">
            <button
              className="border-btn"
              style={{ color: "#F5CA3D", border: "1px solid #F5CA3D" }}
              onClick={showChatBox}
            >
              <img
                loading="lazy"
                src={newChatIcon}
                alt="logo"
                className="mx-1"
                width={isMobile ? 25 : 30}
              />
              Chat with us
            </button>
            <button
              className="standard-btn"
              onClick={goToNextStep}
              disabled={!canProceed}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
